<template>
  <div
    ref="tableContainer"
    class="table-wrapper table-wrapper-fullheight"
  >
    <Spin
      v-show="loading"
      fix
    />
    <div
      v-show="!loading"
      class="ivu-table ivu-table-small"
      :class="{ 'ivu-table-border': border, 'ivu-table-stripe': stripe }"
    >
      <div
        ref="table"
        :style="{ maxHeight: `${tableHeight}px` }"
        class="ivu-table-body ivu-table-overflowX ivu-table-overflowY"
      >
        <table
          v-show="data.length"
          cellspacing="0"
          cellpadding="0"
          border="0"
          :style="{ width: `${tableWidth}px` }"
        >
          <thead
            ref="tableHead"
            class="ivu-table-thead"
          >
            <tr>
              <th
                v-if="additionalColumns.length"
                rowspan="1"
                colspan="1"
                :style="{ width: `${tableHeaderAdditionalLeftColWidth}px`, minWidth: `${tableHeaderAdditionalLeftColWidth}px`, maxWidth: `${tableHeaderAdditionalLeftColWidth}px` }"
                class="ivu-table-column-center"
              />
              <th
                :style="{ width: `${tableHeaderLeftColWidth}px`, minWidth: `${tableHeaderLeftColWidth}px`, maxWidth: `${tableHeaderLeftColWidth}px` }"
                class="ivu-table-column-center"
              >
                <div class="ivu-table-cell">
                  <template v-if="additionalHeaderColumn && additionalHeaderColumn.orderType">
                    <span
                      class="ivu-table-cell-sort"
                      @click="handleSortByHead(additionalHeaderColumn.name, 'header')"
                    >
                      {{ additionalHeaderColumn.displayName || additionalHeaderColumn.name }}
                      <Tooltip
                        v-if="additionalHeaderColumn.info"
                        :content="additionalHeaderColumn.info"
                        transfer
                        max-width="200"
                      >
                        <i class="fas fa-info-circle" />
                      </Tooltip>
                    </span>
                    <span class="ivu-table-sort">
                      <i
                        :class="{on: additionalHeaderColumn.orderType === 'asc'}"
                        class="ivu-icon ivu-icon-md-arrow-dropup"
                        @click="handleSort(additionalHeaderColumn.name, 'asc', 'header')"
                      />
                      <i
                        :class="{on: additionalHeaderColumn.orderType === 'desc'}"
                        class="ivu-icon ivu-icon-md-arrow-dropdown"
                        @click="handleSort(additionalHeaderColumn.name, 'desc', 'header')"
                      />
                    </span>
                  </template>
                  <template v-else-if="additionalHeaderColumn">
                    {{ additionalHeaderColumn.displayName || additionalHeaderColumn.name }}
                    <Tooltip
                      v-if="additionalHeaderColumn.info"
                      :content="additionalHeaderColumn.info"
                      transfer
                      max-width="200"
                    >
                      <i class="fas fa-info-circle" />
                    </Tooltip>
                  </template>
                </div>
              </th>
              <th
                v-for="column in tableHeaderRow"
                :key="column"
                :style="{ width: `${tableColWidth}px`, minWidth: `${tableColWidth}px`, maxWidth: `${tableColWidth}px` }"
                class="ivu-table-column-center"
              >
                <div class="ivu-table-cell">
                  <template v-if="checkOrderType(column)">
                    <span
                      class="ivu-table-cell-sort"
                      @click="handleSortByHead(column)"
                    >
                      <template v-if="numberTypes.includes(headerColumn.format)">
                        {{ formatNumber(column, computedColumns[column].format, computedColumns[column].precision) }}
                      </template>
                      <template v-else-if="['date'].includes(computedColumns[column].format)">
                        {{ $moment(column, computedColumns[column].fromFormat).format(computedColumns[column].toFormat) }}
                      </template>
                      <template v-else>
                        {{ column }}
                      </template>
                    </span>
                    <span class="ivu-table-sort">
                      <i
                        :class="{on: computedColumns[column].orderType === 'asc'}"
                        class="ivu-icon ivu-icon-md-arrow-dropup"
                        @click="handleSort(column, 'asc')"
                      />
                      <i
                        :class="{on: computedColumns[column].orderType === 'desc'}"
                        class="ivu-icon ivu-icon-md-arrow-dropdown"
                        @click="handleSort(column, 'desc')"
                      />
                    </span>
                  </template>
                  <template v-else-if="computedColumns[column]">
                    <template v-if="numberTypes.includes(widget.headerColumn.format)">
                      {{ formatNumber(column, computedColumns[column].format, computedColumns[column].precision) }}
                    </template>
                    <template v-else-if="['date'].includes(computedColumns[column].format)">
                      {{ $moment(column, computedColumns[column].fromFormat).format(computedColumns[column].toFormat) }}
                    </template>
                    <template v-else>
                      {{ column }}
                    </template>
                  </template>
                </div>
              </th>
              <th
                v-if="hasChart"
                rowspan="1"
                colspan="1"
                class="ivu-table-column-center ivu-table-hidden"
              />
            </tr>
          </thead>
          <tbody
            ref="tableBody"
            class="ivu-table-tbody"
            :style="{ height: `${tableBodyHeight}px`, overflow: 'auto'}"
            @scroll.passive="updateSyncedScroll"
          >
            <tr
              v-for="(row, index) in copyOfComputedData"
              :key="`custom-table-body-tr-${index}`"
            >
              <td
                v-if="additionalColumns.length"
                :style="{ visibility: 'hidden', width: `${tableHeaderAdditionalLeftColWidth}px`, minWidth: `${tableHeaderAdditionalLeftColWidth}px`, maxWidth: `${tableHeaderAdditionalLeftColWidth}px` }"
                class="ivu-table-column-center"
              />
              <td
                :style="{ width: `${tableHeaderLeftColWidth}px`, minWidth: `${tableHeaderLeftColWidth}px`, maxWidth: `${tableHeaderLeftColWidth}px` }"
                class="ivu-table-column-center"
              >
                {{ row[additionalHeaderColumn.name] }}
              </td>
              <template v-for="(column, i) in tableHeaderRow">
                <td
                  v-if="checkValueExistance(row, column)"
                  :key="`custom-table-body-td-${i}`"
                  class="ivu-table-column-center"
                  :style="{
                    backgroundColor: columns[aggregationColumn].background ? calculateBgColor(columns[aggregationColumn].background, row[column][columns[aggregationColumn].background.comparedColumn]) : '',
                    color: columns[aggregationColumn].color
                      ? calculateColor(columns[aggregationColumn].color, row[column][columns[aggregationColumn].color.comparedColumn])
                      : (columns[aggregationColumn].background ? pickTextColorBasedOnBgColor(calculateBgColor(columns[aggregationColumn].background, row[column][columns[aggregationColumn].background.comparedColumn])) : 'unset'),
                    width: `${tableColWidth}px`,
                    minWidth: `${tableColWidth}px`,
                    maxWidth: `${tableColWidth}px`
                  }"
                  @mouseenter="$event => handleShowTooltip($event, row, column)"
                  @mouseleave="handleCloseTooltip"
                >
                  <span>
                    <template v-if="numberTypes.includes(columns[aggregationColumn].format)">
                      {{ formatNumber(row[column][aggregationColumn], columns[aggregationColumn].format, columns[aggregationColumn].precision) }}
                    </template>
                    <template v-else-if="['date'].includes(additionalHeaderColumn.format)">
                      {{ $moment(row[column][aggregationColumn], additionalHeaderColumn.fromFormat).format(additionalHeaderColumn.toFormat) }}
                    </template>
                    <template v-else>
                      {{ row[column][aggregationColumn] }}
                    </template>
                  </span>
                </td>
                <td
                  v-else
                  :key="`custom-table-body-td-${i}`"
                  class="ivu-table-column-center"
                />
              </template>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-if="hasChart"
        :style="{width: `${chart.width}px`}"
        class="ivu-table-fixed-right"
        style="right: 0px"
      >
        <div class="ivu-table-fixed-header">
          <table
            cellspacing="0"
            cellpadding="0"
            border="0"
            :style="{width: `${chart.width}px`}"
          >
            <colgroup>
              <col :width="chart.width">
            </colgroup>
            <thead>
              <tr>
                <th class="ivu-table-column-center" />
              </tr>
            </thead>
          </table>
        </div>
        <div class="ivu-table-fixed-body">
          <table
            cellspacing="0"
            cellpadding="0"
            border="0"
            :style="{width: `${chart.width}px`}"
          >
            <tbody class="ivu-table-tbody ivu-table-overflowX">
              <tr
                v-for="(column, index) in Object.keys(columns)"
                :key="`custom-table-fixed-body-tr-${index}`"
                class="ivu-table-row"
              >
                <td class="ivu-table-column-center">
                  <div
                    v-if="chart.type === 'line'"
                    :style="{width: `${chart.width}px`, height: '28px'}"
                    class="ivu-table-cell"
                  >
                    <LineChart :data="computeLineChartData(computedData, chart.xAxis, column)" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div
        v-show="!data.length"
        :style="{ maxHeight: `${tableHeight}px` }"
        class="ivu-table-tip"
      >
        <table
          cellspacing="0"
          cellpadding="0"
          border="0"
        >
          <tbody>
            <tr>
              <td style="width: 1px;">
                {{ noDataText }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <span
      v-show="showTooltip"
      v-transfer-dom
      data-transfer="true"
      :style="{ position: 'absolute', top: `${tooltipTop}px`, left: `${tooltipLeft}px` }"
    >
      <div
        v-if="hasTooltip"
        class="custom-tooltip"
      >
        <p v-if="additionalHeaderColumn">
          {{ additionalHeaderColumn.displayName }}: {{ formatCellByPath([additionalHeaderColumn.name], additionalHeaderColumn.format, additionalHeaderColumn.fromFormat, additionalHeaderColumn.toFormat) }}
        </p>
        <p>
          {{ headerColumn.displayName }}: {{ formatCellByValue(selectedColumn, headerColumn.format, headerColumn.fromFormat, headerColumn.toFormat) }}
        </p>
        <p>
          {{ columns[aggregationColumn].displayName || aggregationColumn }}: {{ formatCellByPath([selectedColumn, aggregationColumn], columns[aggregationColumn].format, columns[aggregationColumn].fromFormat, columns[aggregationColumn].toFormat) }}
        </p>
        <template v-for="tooltipRow in columns[aggregationColumn].tooltip">
          <p
            v-if="tooltipRow.column !== aggregationColumn"
            :key="tooltipRow.column"
          >
            {{ tooltipRow.displayName || tooltipRow.column }}: {{ formatCellByPath([selectedColumn, tooltipRow.column], tooltipRow.format, tooltipRow.fromFormat, tooltipRow.toFormat) }}
          </p>
        </template>
      </div>
    </span>
  </div>
</template>

<script>
import _ from 'lodash'
import LineChart from '@/components/helper-components/chart-components/LineChart'
import CustomTableByValueMixin from '@/mixins/custom-table-by-value-mixin'

export default {
  components: {
    LineChart
  },

  mixins: [ CustomTableByValueMixin ],

  computed: {
    tableColNum () {
      return this.tableHeaderRow.length
    },

    tableBodyHeight () {
      // 32 is the height of the header. Table body height = table height - table head height
      return this.tableHeight - 32 > 0 ? this.tableHeight - 32 : 0
    }
  },

  watch: {
    data () {
      this.computedColumns = this.computeColumns()
      this.additionalHeaderColumn.orderType = 'normal'
      this.scrollTable()
    }
  },

  mounted () {
    this.updateSyncedScroll()
    this.scrollTable()
  },

  updated () {
    this.tableWidth = this.$refs.tableContainer.clientWidth
    this.tableHeight = this.$refs.tableContainer.clientHeight
  },

  methods: {
    /**
     * Scroll table to farther right
     */
    scrollTable () {
      if (this.scroll && _.has(this.$refs.table, 'scrollTo')) {
        this.$refs.tableBody.scrollTo({
          top: 0,
          left: this.$refs.tableBody.scrollWidth,
          behavior: 'smooth'
        })
      }
    },

    /**
     * Auto scroll table head with table body in case there's overflow
     */
    updateSyncedScroll () {
      const tableBody = this.$refs.tableBody
      const tableHead = this.$refs.tableHead
      const position = tableBody.scrollLeft
      if (tableHead.scrollLeft !== position) {
        tableHead.scrollLeft = position
      }
    },

    /**
     * Show tooltip based on mouse enter event
     * @param {Object} $event mouse event
     * @param {Object} row
     * @param {String} column
     */
    handleShowTooltip ($event, row, column) {
      this.selectedRow = row
      this.selectedColumn = column
      this.showTooltip = true
      this.tooltipTop = $event.pageY + 10
      this.tooltipLeft = $event.pageX + 10
    },

    /**
     * Hide tooltip when mouse leave event
     */
    handleCloseTooltip () {
      this.showTooltip = false
    },

    /**
     * Check vale existance in the row with the key column
     * @param {Object} row
     * @param {String} column
     * @return {Boolean} if the param value exists in the row
     */
    checkValueExistance (row, column) {
      return _.has(row, [column, this.aggregationColumn])
    }
  }
}
</script>

<style scoped>
  .table-wrapper {
    border-top: 1px solid var(--color-card-border);
    border-left: 1px solid var(--color-card-border);
    position: relative;
  }
  .table-wrapper-fullheight {
    height: 100%;
  }
  .ivu-table {
    cursor: pointer;
  }
  .ivu-table-body {
    clear: both;
    padding: 0;
    margin-left: auto;
    margin-right: auto;
    overflow: hidden;
  }
  .ivu-table-thead {
    -webkit-box-flex: 0;
    -ms-flex: 0 0 auto;
    flex: 0 0 auto;
    display: block;
    overflow: hidden;
  }
  .ivu-table-tbody {
    display: block;
    -webkit-box-flex: 1;
    -ms-flex: 1 1 auto;
    flex: 1 1 auto;
    overflow: auto;
  }
  .ivu-table-cell {
    padding: 3px 5px;
    word-break: unset;
  }
  .ivu-table-stripe .ivu-table-body tr td:hover {
    background-color: #ebf7ff;
  }
  .ivu-table-small td {
    height: 30px;
  }
  .ivu-table-cell-additional-column {
    writing-mode: tb-rl;
    font-weight: bold;
    font-size: 16px;
    -ms-transform: rotate(-180deg); /* IE 9 */
    -webkit-transform: rotate(-180deg); /* Safari 3-8 */
    transform: rotate(-180deg);
  }
  .ivu-table-column-center pre{
    font-family: 'Montserrat', sans-serif;
    margin: 0;
    text-align: left;
    padding-left: 10px;
  }
</style>
